import React, { useState } from "react";
import { ChevronDown, ChevronUp, Shield, Truck, CreditCard, RefreshCw, FileText, Phone, AlertCircle, Package, Clock, Building, MapPin, Mail, CheckCircle } from "lucide-react";

const TERMS = [
  {
    icon: Truck,
    title: "Delivery Scope & Installation",
    content:
      "Supply only. No onsite service or installation is included. Installation charges for wiring, digging, and related work will be charged separately on an actual basis.",
  },
  {
    icon: CreditCard,
    title: "Pricing & Payment Terms",
    content:
      "All prices displayed are exclusive of GST (18% applicable). 100% advance payment is required for procurement of materials. Our prices are calculated on the Amazon India listed price after removing GST and applying volume-based discounts. Final invoice will include applicable GST. As per company policy, goods are not supplied on a credit basis.",
  },
  {
    icon: FileText,
    title: "Quotation Validity",
    content:
      "This quotation is provided for estimation purposes and is valid for 3 days for the specified quantity. Quotations are subject to change based on market conditions, GST revisions, or supplier price changes. A revised quotation will be issued if the validity period lapses. Orator Hype reserves the right to withdraw a quote at any time before formal order confirmation.",
  },
  {
    icon: Clock,
    title: "Delivery Timeline",
    content:
      "Express Delivery: Within 48 hours (applicable only for locations within 30 km of a major airport). Standard Delivery: 7–10 working days. Delivery timelines are calculated from the date of Purchase Order (PO) confirmation and receipt of full payment (dispatch from Mumbai). Orator Hype is not liable for delays caused by courier partners or force majeure events.",
  },
  {
    icon: RefreshCw,
    title: "Warranty",
    content:
      "Warranty is provided by the respective brand/manufacturer, ranging from 1 to 5 years, depending on the product. Orator Hype facilitates warranty claims at manufacturer service centres. Dead-on-arrival (DOA) products must be reported within 48 hours of receipt. No returns accepted for products that have been installed or tampered with.",
  },
  {
    icon: Shield,
    title: "Genuine Products & GST",
    content:
      "All products supplied by Orator Hype are 100% genuine and sourced from authorised distributors or directly from manufacturers. Products come with original manufacturer packaging, manuals, and accessories. Applicable GST @ 18% will be charged extra as per billing requirements. We provide GST invoices for all purchases.",
  },
  {
    icon: Package,
    title: "Purchase Order Requirements",
    content:
      "The order will be processed only upon receipt of an official Purchase Order (PO). Kindly ensure that the Purchase Order (PO) includes complete delivery details, including contact person's name, phone number, full address, and PIN code. Pricing and availability are subject to confirmation at the time of order.",
  },
  {
    icon: MapPin,
    title: "Express Delivery Conditions",
    content:
      "Express Delivery (48 hours) is applicable only for locations within 30 km of a major airport. Express delivery charges are added to the quoted price at ₹450 per kg per unit. Once payment is received and the order is dispatched, a tracking ID will be shared for real-time shipment tracking.",
  },
  {
    icon: Phone,
    title: "Support & Contact",
    content:
      "For any queries or assistance, please feel free to call or WhatsApp us at +91 73046 60050. For escalations, write to aryan@oratorhype.com. Our team will respond within 2 business hours during working days (Mon–Sat, 10am–7pm IST).",
  },
];

const TermItem = ({ term }) => {
  const [open, setOpen] = useState(false);
  const Icon = term.icon;
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden bg-white/80 backdrop-blur-sm hover:shadow-lg transition-all duration-300">
      <button
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 bg-white hover:bg-gray-50 transition text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-md">
            <Icon className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-gray-800 text-sm md:text-base">{term.title}</span>
        </div>
        {open ? (
          <ChevronUp className="h-4 w-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="px-5 pb-4 pt-1 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100">
          <p className="text-sm text-gray-600 leading-relaxed">{term.content}</p>
        </div>
      )}
    </div>
  );
};

const TermsSection = () => {
  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-blue-100 rounded-full px-4 py-1.5 mb-4">
            <FileText className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-blue-700">Legal Information</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Terms & Conditions</h2>
          <p className="text-gray-500 text-sm md:text-base max-w-2xl mx-auto">
            Please review our terms carefully before placing an order
          </p>
        </div>

        {/* Important Notes Section */}
        <div className="mb-8 bg-gradient-to-r from-amber-50 to-orange-50 border-l-4 border-amber-500 rounded-xl p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                Important Notes
                <span className="text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Please Read</span>
              </h3>
              <ul className="space-y-2 text-sm text-amber-800/90">
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>The order will be processed only upon receipt of an official Purchase Order (PO)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>Pricing and availability are subject to confirmation at the time of order</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>Kindly ensure the PO includes complete delivery details (contact person, phone, address, PIN code)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>Once payment is received and order dispatched, a tracking ID will be shared</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Terms Accordion */}
        <div className="space-y-3">
          {TERMS.map((term) => (
            <TermItem key={term.title} term={term} />
          ))}
        </div>

        {/* Quick Info Grid */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 text-center border border-blue-100">
            <Clock className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <div className="text-xs font-medium text-blue-700">Quote Validity</div>
            <div className="text-sm font-bold text-gray-800">3 Days</div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 text-center border border-green-100">
            <CreditCard className="h-6 w-6 text-green-600 mx-auto mb-2" />
            <div className="text-xs font-medium text-green-700">Payment Terms</div>
            <div className="text-sm font-bold text-gray-800">100% Advance</div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 text-center border border-purple-100">
            <Truck className="h-6 w-6 text-purple-600 mx-auto mb-2" />
            <div className="text-xs font-medium text-purple-700">Express Delivery</div>
            <div className="text-sm font-bold text-gray-800">48 Hours*</div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 text-center border border-orange-100">
            <Shield className="h-6 w-6 text-orange-600 mx-auto mb-2" />
            <div className="text-xs font-medium text-orange-700">Warranty</div>
            <div className="text-sm font-bold text-gray-800">1-5 Years</div>
          </div>
        </div>

        {/* Contact CTA */}
        <div className="mt-10 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl p-6 md:p-8 text-center text-white shadow-xl">
          <div className="max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-1.5 mb-4 backdrop-blur-sm">
              <Phone className="h-4 w-4" />
              <span className="text-sm font-medium">24/7 Support Available</span>
            </div>
            <p className="font-semibold text-xl md:text-2xl mb-2">Need a custom deal?</p>
            <p className="text-blue-100 text-sm md:text-base mb-6">
              For large volume orders (500+ units) or enterprise contracts, reach out directly to our sales team.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="mailto:sales@oratorhype.com"
                className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 font-semibold px-6 py-3 rounded-xl text-sm hover:bg-blue-50 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <Mail className="h-4 w-4" />
                sales@oratorhype.com
              </a>
              <a
                href="tel:+917304660050"
                className="inline-flex items-center justify-center gap-2 bg-blue-500 text-white font-semibold px-6 py-3 rounded-xl text-sm hover:bg-blue-400 transition-all duration-200 shadow-lg hover:shadow-xl border border-white/20"
              >
                <Phone className="h-4 w-4" />
                +91 73046 60050
              </a>
            </div>
            <p className="text-xs text-blue-200 mt-4">
              *Express Delivery (48 hours) applicable only for locations within 30km of a major airport
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TermsSection;